import type { ReactNode } from 'react';
import { Icon, type IconName } from '@evogard/ui';

/**
 * A célula das tabelas do sinistro: um ícone que diz QUE dado é aquele, e o
 * dado — numa linha só.
 *
 * ## Uma linha, sempre (decisão do dono, 25/09/2026)
 *
 * A segunda linha em cinza embaixo do dado deixava a linha da tabela confusa:
 * o olho não sabia se a linha era uma ou duas, nem qual das duas pertencia à
 * coluna. Por isso esta peça não tem onde pôr uma segunda linha: o conteúdo é
 * uma fileira só, cortada com reticências, e o texto inteiro vai na dica
 * (`hint`). O que precisa aparecer vai na mesma linha, numa coluna própria, num
 * selo ou na ficha.
 *
 * ## Um ícone por tipo de dado, e não por valor
 *
 * O carro é o veículo, o calendário é a data, o quadro é a fase — iguais em
 * todas as linhas da coluna. É o que deixa a pessoa achar a coluna pelo
 * símbolo, sem ler o cabeçalho, que é o ganho que se queria. Ícone por VALOR
 * ("colisão" com um desenho, "roubo" com outro) seria escolher dez metáforas
 * que ninguém decidiu, e a tabela passaria a afirmar uma leitura que não é de
 * ninguém. A exceção é onde o valor É o fato: o sinistro do associado e o do
 * terceiro são coisas diferentes, e o ícone muda.
 *
 * ## Por que mora no módulo, e não na `Table`
 *
 * A `Table` tem trinta telas em cima dela, e `cell` já devolve qualquer
 * conteúdo — a tabela não precisa saber de ícone nenhum. Mudar o componente
 * mudaria as trinta; esta peça muda só as do sinistro. Se outra tela quiser a
 * mesma célula, ela sobe para `@evogard/ui` com o motivo, e não antes.
 *
 * O quadro é sempre neutro. Na cor da marca ele pareceria ação ou estado
 * (decisão 5 do `DESIGN.md`), e não é nenhum dos dois; a coluna que identifica
 * a linha se destaca pelo peso do número, não pela cor do quadro.
 *
 * O quadro tem 32px, a mesma medida do `Avatar` `md` — é o que deixa a coluna
 * de nome do terceiro, com iniciais, alinhar com as colunas de ícone.
 */
export function DataCell({
  icon,
  children,
  hint,
}: {
  icon: IconName;
  /** O dado, numa linha. O que não couber é cortado com reticências. */
  children: ReactNode;
  /** O texto inteiro na dica do ponteiro, para quando a linha corta. */
  hint?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2.5">
      <span
        aria-hidden="true"
        className="flex size-8 shrink-0 items-center justify-center rounded-pequeno bg-superficie-suave text-texto-suave"
      >
        <Icon nome={icon} size={16} />
      </span>
      <div className="flex min-w-0 items-center gap-2 truncate" title={hint}>
        {children}
      </div>
    </div>
  );
}
